import { z } from 'zod';

import {
  captchaTokenField,
  emailField,
  passwordField,
  passwordStrengthPattern,
} from '../common/fields.js';

/**
 * Schemas de autenticação compartilhados entre web (formulários) e back
 * (Edge Functions que processam signup/reset/validam payloads). Mensagens
 * são **chaves i18n** resolvidas no consumo.
 */

/** Formulário de login: email + senha + captcha. */
export const signInSchema = z.object({
  email: emailField,
  password: z.string().min(1, 'validation:password.required'),
  captchaToken: captchaTokenField,
});

export type SignInInput = z.infer<typeof signInSchema>;

/** Formulário de signup: email + senha forte + confirmação + captcha. */
export const signUpSchema = z
  .object({
    email: emailField,
    password: passwordField.regex(passwordStrengthPattern, 'validation:password.strength'),
    passwordConfirmation: z.string().min(1, 'validation:password.confirmRequired'),
    captchaToken: captchaTokenField,
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'validation:password.mismatch',
  });

export type SignUpInput = z.infer<typeof signUpSchema>;

/** Formulário de "enviar link de redefinição" (só email + captcha). */
export const resetPasswordRequestSchema = z.object({
  email: emailField,
  captchaToken: captchaTokenField,
});

export type ResetPasswordRequestInput = z.infer<typeof resetPasswordRequestSchema>;

/** Formulário de "digite nova senha" após o callback do email. */
export const resetPasswordConfirmSchema = z
  .object({
    password: passwordField.regex(passwordStrengthPattern, 'validation:password.strength'),
    passwordConfirmation: z.string().min(1, 'validation:password.confirmRequired'),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'validation:password.mismatch',
  });

export type ResetPasswordConfirmInput = z.infer<typeof resetPasswordConfirmSchema>;
